"use client";
import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "antd";
import { FaExclamationTriangle, FaLock } from "react-icons/fa";
import { useLogoutMutation } from "@/graphql/auth";
import { useRouter } from "next/navigation";
import { client } from "@/graphql/lib/apollo";
import { toast } from "react-toastify";
import { useTranslations } from "next-intl";

interface BlockedPopupProps {
    isVisible: boolean;
    reason?: string;
}

const BlockedPopup: React.FC<BlockedPopupProps> = ({
    isVisible,
    reason = "Violation of Warfa Platform terms of service",
}) => {
    const [isOpen, setIsOpen] = useState(isVisible);
    const router = useRouter();
    const [logout] = useLogoutMutation();
    const t = useTranslations("block")

    useEffect(() => {
        setIsOpen(isVisible);
    }, [isVisible]);

    const handleLogout = async () => {
        try {
            const { data } = await logout();
            if (data?.logout.success) {
                await client.clearStore();
                toast.success("تم تسجيل الخروج")
                router.push("/sign-in");
            }
        } catch {
            toast.error("فشل تسجيل الخروج")
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4 bg-black/80 pointer-events-auto">
                    <motion.div
                        initial={{ scale: 0.8, opacity: 0, y: 20 }}
                        animate={{ scale: 1, opacity: 1, y: 0 }}
                        exit={{ scale: 0.8, opacity: 0, y: 20 }}
                        transition={{ type: "spring", damping: 20, stiffness: 300 }}
                        className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden"
                    >
                        {/* Header */}
                        <div className="bg-[#708238] text-white p-6 text-center relative overflow-hidden">
                            <div className="absolute top-0 left-0 w-full h-1 bg-[#C9B037]" />
                            <motion.div
                                initial={{ scale: 0 }}
                                animate={{ scale: 1 }}
                                transition={{ delay: 0.2, type: "spring" }}
                                className="flex justify-center mb-4"
                            >
                                <div className="relative">
                                    <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                                        <FaLock className="text-2xl text-white" />
                                    </div>
                                    <motion.div
                                        animate={{ rotate: 360 }}
                                        transition={{ duration: 2, repeat: Infinity, ease: "linear" }}
                                        className="absolute -inset-2 border-2 border-[#C9B037] border-t-transparent rounded-full"
                                    />
                                </div>
                            </motion.div>

                            <h2 className="text-2xl font-bold mb-2">{t("accountRestricted")}</h2>
                            {/* <p className="text-white text-opacity-90">
                                Your access to <strong>Warfa Platform</strong> has been temporarily suspended
                            </p> */}
                            <p className="text-white text-opacity-90" dangerouslySetInnerHTML={{ __html: t.raw("accessSuspended") }} />
                        </div>

                        {/* Body */}
                        <div className="p-6 space-y-4">
                            <div className="flex items-start space-x-3">
                                <FaExclamationTriangle className="text-[#C9B037] text-lg mt-1 flex-shrink-0" />
                                <div>
                                    <h3 className="font-semibold text-gray-800">{t("violationDetected")}</h3>
                                    <p className="text-gray-600 text-sm mt-1">{t("reason")}</p>
                                </div>
                            </div>

                            <div className="bg-orange-50 border border-orange-200 rounded-lg p-4 mt-4">
                                <p className="text-sm text-orange-800 text-center" dangerouslySetInnerHTML={{ __html: t.raw("note") }} />
                            </div>
                        </div>

                        {/* Footer */}
                        <div className="bg-gray-50 px-6 py-4 border-t border-gray-200 flex flex-col sm:flex-row justify-center gap-3">
                            <Button
                                type="primary"
                                className="!bg-[#708238] hover:!bg-[#5a6a2d] !border-[#708238] hover:!border-[#5a6a2d]"
                                onClick={() =>
                                    (window.location.href = "mailto:support@wafra-platform.com")
                                }
                            >
                                {t("contactSupport")}
                            </Button>
                            <Button
                                danger
                                className="!border-red-600 hover:!bg-red-600 hover:!text-white"
                                onClick={handleLogout}
                            >
                                {t("logout")}
                            </Button>
                        </div>
                    </motion.div>
                </div>
            )}
        </AnimatePresence>
    );
};

export default BlockedPopup;
